import { ElementRef, Injectable, OnInit } from '@angular/core';
import { ColorPicker } from '../../../models/colorPicker.model';
import { FileData } from '../../../models/fileData.model';
import { Schedule } from '../../../models/schedule.model';
import { jwtDecode } from "jwt-decode";
import { TranslationService } from '../../translation/translation.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { ContentAddress } from '../../../models/contentAddress.model';
import { ContentUrl } from '../../../models/contentUrl.model';
import { RefBusiness } from '../../../models/refBusiness.model';
import { ContentActivityPrestations } from '../../../models/contentActivityPrestations.model';
import { ContentIntitule } from '../../../models/contentIntitule.model';
import { DomSanitizer } from '@angular/platform-browser';
// @ts-ignore
import { html as beautify } from 'js-beautify';
import { AppStateService } from '../app-state/app-state.service';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  regexTwitterUrl: RegExp = /^(?:https?:\/\/)?(?:www\.|m\.|mobile\.)?(?:twitter\.com|x\.com)\/([A-Za-z0-9_]{1,15})(?:[/?#].*)?$/;
  constructor(private translationService: TranslationService, private snackBar: SnackbarService, private sanitizer: DomSanitizer, private appState: AppStateService) {}

  generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  generateRandomHexColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  rgbaToHex(rgba: string): string {
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);

    if (!match) {
      throw new Error("Formato de cor inválido");
    }

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

    const hexR = r.toString(16).padStart(2, '0');
    const hexG = g.toString(16).padStart(2, '0');
    const hexB = b.toString(16).padStart(2, '0');
    const hexA = Math.round(a * 255).toString(16).padStart(2, '0');

    return `#${hexR}${hexG}${hexB}${hexA}`;
  }


  hexToRgba(hex: string): string {
    hex = hex.replace(/^#/, '');

    let alpha = 'FF';

    if (hex.length === 8) {
      alpha = hex.slice(6, 8);
      hex = hex.slice(0, 6);
    }

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    const a = parseInt(alpha, 16) / 255;

    return `rgba(${r},${g},${b},${a})`;
  }

  checkInputValuesRegex(values: string[], regex: RegExp): boolean {
    for (const value of values) {
      if (!this.verifyRegex(value, regex)) {
        return false;
      }
    }
    return true;
  }


  checkInputOrSelectFilled(values: string[], minQuantity: number): boolean {
    if (!values) {
      return false;
    }

    const validValuesCount = values.filter(value => value.trim() !== "" && value !== null && value !== undefined).length;

    return validValuesCount >= minQuantity;
  }

  checkColorPickerFilled(values: ColorPicker[], hasColorText: boolean, minQuantity: number): boolean {
    if (!values) {
      return false;
    }

    const validValuesCount = values.filter(value => {
      const hasValidColor = value.color && !this.hasAlphaZero(value.color);
      const hasValidText = !hasColorText || (hasColorText && value.text && value.text.trim() !== "");

      return hasValidColor || hasValidText;
    }).length;

    return validValuesCount >= minQuantity;
  }

  checkAddressFilled(values: ContentAddress[]): boolean {
    return values && values.every(address =>
      address.extendedWayLabel && //|| (address.number != undefined && address.trackType && address.laneLabel)) &&
      address.localityLabel &&
      address.postalCode &&
      address.countryCode
    );
  }
  checkAddressFullEmpty(values: ContentAddress[]): boolean {
    return values && values.every(address =>
      (address.extendedWayLabel == undefined || address.extendedWayLabel == "") &&
      (address.localityLabel == undefined || address.localityLabel == "") &&
      (address.postalCode == undefined || address.postalCode == "") &&
      (address.countryCode == undefined || address.countryCode == "")
    );
  }

  checkIntituleFilled(values: ContentIntitule[]): boolean{
    return values && values.every(intitule => intitule.denomination);
  }

  checkUrlsFilled(urls: ContentUrl[]): boolean{
    return urls && urls.every(url => this.checkUrlFilled(url));
  }

  checkUrlFilled(url: ContentUrl): boolean{
    return url != undefined && url.label != undefined && url.label != "" && url.type != undefined && url.type != "";
  }

  checkUrlFullEmpty(url: ContentUrl): boolean{
    return url == undefined || ((url.label == undefined || url.label == "") && (url.type == undefined || url.type == ""));
  }

  checkUrlsWellFilled(values: ContentUrl[], urlTypes: RefBusiness[]): boolean{
    return values && values.every(url => {
      return this.checkUrlWellFilled(url, urlTypes);
    });
  }

  checkUrlWellFilled(url: ContentUrl, urlTypes: RefBusiness[]): boolean{
    if (!url.label || !url.type) return false;

    if(!this.urlHasGoodStructure(url.label))
    {
      return false;
    }

    const typeDef = urlTypes.find(t => t.code === url.type);
    if (!typeDef) return false;

    if (typeDef.domains && typeDef.domains.length > 0) {
      var urlModel = new URL(url.label)
      var urlHost = urlModel.host;

      return typeDef.domains.some(domain => urlHost.endsWith(domain.toLowerCase()));
    }

    if(url.type == "TWITTER" && !this.twitterUrlHasGoodStructure(url.label)){
      return false;
    }

    return true;
  }

  checkActivityPrestationsFilled(values: ContentActivityPrestations[]): boolean {
    return values && values.length > 0 && values.every(v => v.activity && v.activity.code.length > 0);
  }

  checkVisualPublicationFilled(values: FileData[]): boolean {
    return values && values.length > 0;
  }

  checkFileFilled(values: FileData[]): boolean {
    return values && values.length > 0;
  }

  checkScheduleFilled(values: Schedule[]): boolean {
    return values && values.every(schedule =>
      (schedule.monday.periods && schedule.monday.periods.length > 0)
      || (schedule.tuesday.periods && schedule.tuesday.periods.length > 0)
      || (schedule.wednesday.periods && schedule.wednesday.periods.length > 0)
      || (schedule.thursday.periods && schedule.thursday.periods.length > 0)
      || (schedule.friday.periods && schedule.friday.periods.length > 0)
      || (schedule.saturday.periods && schedule.saturday.periods.length > 0)
      || (schedule.sunday.periods && schedule.sunday.periods.length > 0)
    );
  }

  hasAlphaZero(color: string): boolean {
    if (color.startsWith('rgba')) {
      const alpha = parseFloat(color.slice(-2, -1));
      return alpha <= 0;
    }
    else if (color.startsWith('#')) {
      const hex = color.replace(/^#/, '');
      if (hex.length === 8) {
        const alphaHex = hex.slice(6, 8);
        return alphaHex == "00";
      }
    }
    return false;
  }

  adjustTextareaHeight(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    var height = textarea.scrollHeight < 36 ? 36 : textarea.scrollHeight;
    textarea.style.height = height + 'px';
  }

  isTextTruncated(element: ElementRef | HTMLElement): boolean {
    const domElement = element instanceof ElementRef ? element.nativeElement : element;
    return domElement.scrollWidth > domElement.clientWidth;
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (Error) {
      return null;
    }
  }

  // convert "yyyy/MM/dd HH:mm:ss" in date format
  parseDateString(dateString: string): Date | undefined {
    if (dateString === null || dateString === undefined || dateString === "") {
      return undefined;
    }

    const [datePart, timePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);

    return new Date(year, month - 1, day, hours, minutes, seconds);
  }


  updateTextProperty(object: any, property: string, regexValidator: RegExp, regexReplace: RegExp) {
    if (property === "") {
      object = object?.toUpperCase();

      if (object && !this.verifyRegex(object, regexValidator)) {
        this.snackBar.showError(this.translationService.translate('Invalid character.'));
        object = object.replace(regexReplace, "");
      }
      return object;
    }
    else {
      object[property] = object[property]?.toUpperCase();

      if (object[property] && !this.verifyRegex(object[property], regexValidator)) {
        this.snackBar.showError(this.translationService.translate('Invalid character.'));
        object[property] = object[property].replace(regexReplace, "");
      }
      return object[property];
    }
  }

  verifyRegex(value: string, regex: RegExp): boolean {
    return regex.test(value);
  }

  getRootZoom(): number {
    const app_root = document.querySelector(`app-root`) as HTMLElement;
    const zoom = parseFloat(app_root.style.getPropertyValue('zoom')) || 1;

    return zoom;
  }

  preventScrollOutside(event: WheelEvent, element: HTMLElement) {
    if (this.isDesktop()) {
      const delta = event.deltaY;
      const atTop = element.scrollTop === 0;
      const zoom = this.getRootZoom();
      const atBottom = element.scrollHeight - element.scrollTop <= (element.clientHeight / zoom);

      if (atTop && delta < 0) {
        event.preventDefault();
      }

      if (atBottom && delta > 0) {
        event.preventDefault();
      }
    }
  }

  // Method to get the current value of `isSmartphone` (Mobile).
  isSmartphone(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  // Method to get the current value of `isTablet`.
  isTablet(): boolean {
    return window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;
  }

  // Method to get the current value of `isDesktop`.
  isDesktop(): boolean {
    return !window.matchMedia('(max-width: 1023px)').matches && !this.appState.getIsEmbed();
  }

  getTotalHeight(element: HTMLElement | null): number {
    if (element) {
      const elementStyles = getComputedStyle(element);
      const height = element.offsetHeight;
      const marginTop = parseFloat(elementStyles.marginTop);
      const marginBottom = parseFloat(elementStyles.marginBottom);
      return height + marginTop + marginBottom;
    } else {
      return 0;
    }
  };

  getTotalWidth(element: HTMLElement): number {
    if (element) {
      const elementStyles = getComputedStyle(element);
      const width = element.offsetWidth;
      const marginLeft = parseFloat(elementStyles.marginLeft);
      const marginRight = parseFloat(elementStyles.marginRight);
      return width + marginLeft + marginRight;
    } else {
      return 0;
    }
  };

  urlHasGoodStructure(url: string) {
    const withoutProtocol = url.replace(/^https?:\/\//, '');
    var testHttp = /^(https?:\/\/).+/.test(url);
    if(!testHttp)
      return testHttp;

    return /^[^\.]+\.[^.]{2,}(\.[^.]{2,})*$/.test(withoutProtocol);
  }


  twitterUrlHasGoodStructure(url: string) {
    if (!this.regexTwitterUrl.test(url)) return false;

    const match = url.match(this.regexTwitterUrl);
    const username = match?.[1];

    if (!username || username.trim() === "") return false;

    return true;
  }

  sanitizeHtml(text: string | undefined) {
    if (!text) return '';

    const hasHtml = this.containsHtml(text);
    let processed = text;

    if (!hasHtml) {
      processed = processed.replace(/\n/g, '<br>');
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(processed, 'text/html');

    // Process all anchor tags
    doc.querySelectorAll<HTMLAnchorElement>('a').forEach(a => {
      // Force target _blank
      a.setAttribute('target', '_blank');
      // Security best practice for _blank
      a.setAttribute('rel', 'noopener noreferrer');

      // href logic
      if (!this.isAndroidOrWindows()) {
        const href = a.getAttribute('href');
        if (href) {
          a.setAttribute('data-href', href);
          a.removeAttribute('href');
        }
      }
    });

    const html = doc.body.innerHTML + ' ';
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  containsHtml(str: string): boolean {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return Array.from(doc.body.childNodes).some(
      node => node.nodeType === 1
    );
  }

  stripHtml(html: string | undefined): string {
    if (!html || html == undefined) return '';

    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || ''
    return text.trim();
  }

  minifyHtml(text: string|undefined): string{
    if (!text) return '';
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(text);

    if(hasHtml){
      return text
      .replace(/\n\s*/g, '')   // Remove line breaks and any whitespace immediately following them
      .replace(/\s{2,}/g, ' ') // Collapse multiple consecutive spaces into a single space
      .trim();
    }

    return text;
  }

  beautifyHtml(text: string | undefined): string {
    if (!text) return '';

    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(text);
    if (!hasHtml) return text;

    return beautify(text, {
      indent_size: 2,
      wrap_line_length: 120,
      preserve_newlines: true,
      max_preserve_newlines: 2,
      inline: []
    });
  }

  isAndroidOrWindows(): boolean {
    const ua = navigator.userAgent;
    const isAndroidOrWindows = /Android|Windows/.test(ua);
    return isAndroidOrWindows;
  }

  VersionBoldIndex(text: string | undefined): string {
    if (!text) return "";

    return text.replace(
      /([a-f0-9-]{36})\.(\d+)\./i,
      '$1.<b>$2</b>.'
    );
  }

  isStringOnlyNumeric(value: string | null | undefined): boolean {
    if (value === null || value === undefined) return false;

    const cleanValue = value.toString().trim();

    const numericRegex = /^-?\d+(\.\d+)?$/;
    return numericRegex.test(cleanValue);
  }

  blockDecimal(event: KeyboardEvent) {
    const char = event.key;
    if (char === '.' || char === ',' || char === 'e' || char === 'E' || char === '+') {
      event.preventDefault();
    }
  }

  normalizeNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) {
      return 0;
    }

    const n = Math.floor(Number(value));
    return Math.max(0, n);
  }

  isValidJson(value: string): boolean {
    if (typeof value !== 'string') return false;

    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null;
    } catch {
      return false;
    }
  }

  beautifyJSON(json: string): string | undefined {
    try {
      const obj = JSON.parse(json);
      json = JSON.stringify(obj, null, 2);
      return json;
    } catch (e) {
      return undefined;
    }
  }

  minifyJSON(json: string): string | undefined {
    try {
      const obj = JSON.parse(json);
      json = JSON.stringify(obj);
      return json;
    } catch (e) {
      return undefined;
    }
  }
}
